/**
 * Canonical permission catalog. Persisted as `varchar` strings (never as a
 * Postgres ENUM) — see CLAUDE.md > CRITICAL Rules > Enums Over Literals and
 * the to-58 design D2. Adding a value requires (a) a new enum member and
 * (b) updating any bundles in {@link Role} that should grant it.
 */
export enum Permission {
	CUSTOMER_READ = 'customer.read',
	CUSTOMER_WRITE = 'customer.write',
	CUSTOMER_PII_READ = 'customer.pii.read',
	CUSTOMER_PII_WRITE = 'customer.pii.write',
	// Customer model v2 (TO-418). READ_ALL widens visibility from the caller's
	// org-unit subtree to the whole tenant; ASSIGN gates reassignment (the
	// assignment history is an audit trail); CONSENT_WRITE records marketing /
	// profiling consent; ATTRIBUTES_WRITE is the partner key/value surface
	// (usually an integration); ANONYMIZE is GDPR erasure.
	CUSTOMER_READ_ALL = 'customer.read.all',
	CUSTOMER_ASSIGN = 'customer.assign',
	CUSTOMER_CONSENT_WRITE = 'customer.consent.write',
	CUSTOMER_ATTRIBUTES_WRITE = 'customer.attributes.write',
	CUSTOMER_ANONYMIZE = 'customer.anonymize',

	OFFER_READ = 'offer.read',
	OFFER_WRITE = 'offer.write',
	OFFER_PUBLISH = 'offer.publish',
	OFFER_ARCHIVE = 'offer.archive',

	// Short-URL shortener (TO-281): minting a code and resolving it via the
	// authenticated/back-office path. The PUBLIC redirect (GET /s/{code}) is
	// unauthenticated and needs no permission. Distinct from offer.* because the
	// shortener is a generic service (offer OR arbitrary-url targets), reusable
	// beyond offers.
	SHORT_URL_CREATE = 'short_url.create',
	SHORT_URL_RESOLVE = 'short_url.resolve',

	TEMPLATE_READ = 'template.read',
	TEMPLATE_WRITE = 'template.write',

	PRODUCT_READ = 'product.read',
	PRODUCT_WRITE = 'product.write',
	PRODUCT_DELETE = 'product.delete',

	// USER_CREATE is deliberately separate from USER_WRITE: provisioning mints a
	// new Cognito identity + `users` row in a tenant, which is a strictly larger
	// authority than editing a user that already exists (TO-303, REQ-001).
	USER_CREATE = 'user.create',
	USER_READ = 'user.read',
	USER_WRITE = 'user.write',
	USER_DELETE = 'user.delete',
	ROLE_ASSIGN = 'role.assign',
	// Tenant-admin user management (TO-307): changing a user's role set and
	// deactivating a user are held by ORG_ADMIN and above only — narrower than
	// `role.assign`, which ORG_MANAGER also holds.
	USER_MANAGE_ROLES = 'user.manage_roles',
	USER_DEACTIVATE = 'user.deactivate',

	// Inbound webhook ledger (TO-323). `WEBHOOK_MANAGE` registers a delivery
	// endpoint, which mints a signing secret an outside system can then use to
	// write into the tenant's ledger — a strictly larger authority than
	// `WEBHOOK_READ`, which only lists what already arrived.
	WEBHOOK_READ = 'webhook.read',
	WEBHOOK_MANAGE = 'webhook.manage',
	// File storage (TO-324). The per-file OWNERSHIP check — not the role — is what
	// scopes a file, so every built-in role carries all three — except the
	// read-mostly PLATFORM_ADMIN, which keeps only `file.read` (TO-626): a caller
	// may only ever reach a file they created. The permission gates the
	// OPERATION; ownership gates the FILE.
	FILE_READ = 'file.read',
	FILE_WRITE = 'file.write',
	FILE_DELETE = 'file.delete',

	AUDIT_READ = 'audit.read',
	AUDIT_EXPORT = 'audit.export',

	TENANT_READ = 'tenant.read',
	TENANT_MANAGE = 'tenant.manage',
	// SSO configuration (TO-308): attach/rotate/test/disable a tenant's SAML IdP.
	// Held by the tenant's own ORG_ADMIN; cross-tenant, only SUPER_ADMIN (ADR-0063).
	// Distinct from `tenant.manage`, which ORG_ADMIN must never hold.
	TENANT_MANAGE_IDP = 'tenant.manage_idp',
	// Read-only view of a tenant's SSO/IdP, attribute-mapping, SCIM and
	// sign-in-policy configuration (TO-626, ADR-0063) — the read counterpart
	// `tenant.manage_idp` never had. Held by PLATFORM_ADMIN only.
	TENANT_IDP_READ = 'tenant.idp.read',
	// Tenant locale settings (TO-415): enabled/required/default content locales.
	// Held by the tenant's own ORG_ADMIN; cross-tenant, only SUPER_ADMIN (ADR-0063).
	// Distinct from `tenant.manage`, which ORG_ADMIN must never hold.
	TENANT_MANAGE_SETTINGS = 'tenant.manage_settings',
	PLATFORM_AUDIT = 'platform.audit',
	// Read tenants across the platform (TO-626, ADR-0063): the `/admin/tenants`
	// read tier and GET-only cross-tenant reads on `/t/{slug}/admin/*`. Distinct
	// from `tenant.read`, which ORG_ADMIN and ORG_MANAGER hold for their own
	// tenant and so cannot gate a cross-tenant read. Held by PLATFORM_ADMIN only.
	PLATFORM_TENANT_READ = 'platform.tenant_read',
	// Read the platform brand catalogue (TO-626, ADR-0063). Held by
	// PLATFORM_ADMIN only; selecting a tenant's brand stays SUPER_ADMIN.
	PLATFORM_BRAND_READ = 'platform_brand.read',
	// Tenant configuration console, tenant plane (TO-374): a Tenant Admin opening
	// their OWN tenant's console. Never grants another tenant — the platform plane
	// is gated on the SUPER_ADMIN role instead.
	TENANT_CONFIG_MANAGE = 'tenant_config.manage',

	// Prompt library (TO-327). Fine-grained so publishing — which freezes an
	// immutable, reproducible version and can move the `production` label — and
	// tenant-wide config (per-type defaults + the eval gate) are strictly larger
	// authorities than editing a draft head. Mirrors the OFFER_* split.
	PROMPT_READ = 'prompt.read',
	PROMPT_WRITE = 'prompt.write',
	PROMPT_PUBLISH = 'prompt.publish',
	PROMPT_EVAL_RUN = 'prompt.eval_run',
	PROMPT_ADMIN = 'prompt.admin',

	// Organization (TO-411). Reading the tree is broad; editing units and
	// configuring the tenant's unit-type taxonomy + hierarchy rules are split so a
	// unit editor cannot reshape the vocabulary every unit is classified by.
	ORGANIZATION_UNIT_READ = 'organization_unit.read',
	ORGANIZATION_UNIT_WRITE = 'organization_unit.write',
	ORGANIZATION_TYPE_READ = 'organization_type.read',
	ORGANIZATION_TYPE_WRITE = 'organization_type.write',
	// User ↔ unit assignments (TO-412). Who sits where is directory data, so reading
	// is limited to user-administering roles; placing users is an org-admin act.
	ORGANIZATION_ASSIGNMENT_READ = 'organization_assignment.read',
	ORGANIZATION_ASSIGNMENT_WRITE = 'organization_assignment.write',

	// Content (TO-419): versioned, approvable texts (legal first). Reading is
	// broad; authoring, approving, publishing, assigning and configuring content
	// types are separate authorities so four-eyes can be staffed by role.
	CONTENT_READ = 'content.read',
	CONTENT_WRITE = 'content.write',
	CONTENT_APPROVE = 'content.approve',
	CONTENT_PUBLISH = 'content.publish',
	CONTENT_ASSIGN = 'content.assign',
	CONTENT_TYPES_MANAGE = 'content.types.manage',

	// File exchange (TO-391): read and replace the tenant's logical file
	// configuration. Held by the tenant's own ORG_ADMIN; the platform routes are
	// gated on the SUPER_ADMIN role instead.
	FILE_EXCHANGE_MANAGE = 'file_exchange.manage',

	// File exchange uploads (TO-392): start, complete and read the caller's own
	// tenant's file uploads (`api` / `web` transports). Separate from
	// `file_exchange.manage` so an operator role can later upload without
	// configuring. Held by the tenant's own ORG_ADMIN.
	FILE_EXCHANGE_UPLOAD = 'file_exchange.upload',

	// Import definitions (TO-414): read, and author/activate/retire, the tenant's
	// versioned import contracts. Held by the tenant's own ORG_ADMIN; the
	// platform routes are gated on the SUPER_ADMIN role instead.
	IMPORT_DEFINITION_READ = 'import_definition.read',
	IMPORT_DEFINITION_MANAGE = 'import_definition.manage',

	// Import monitoring (TO-399): list the tenant's import executions, open one,
	// page its change log and download F8's errors report. Held by the tenant's
	// own ORG_ADMIN.
	IMPORT_EXECUTION_READ = 'import_execution.read',
	// The original imported file (TO-399): unredacted customer data, so a
	// separate grant held by no tenant role (SUPER_ADMIN holds every permission).
	// Every download link is audited.
	IMPORT_EXECUTION_ORIGINAL_FILE_READ = 'import_execution.original_file.read',

	// Notification settings (TO-382, TO-383): read and replace the tenant's own
	// email sender settings and SMS sending configuration, verify its sending
	// domain, send a test SMS, and switch notification types on or off per
	// channel. Held by the tenant's own ORG_ADMIN; the platform routes are gated
	// on the SUPER_ADMIN role instead.
	NOTIFICATION_SETTINGS_MANAGE = 'notification_settings.manage',

	// PDF settings (TO-495): read and replace the tenant's own offer-PDF
	// watermark. Held by the tenant's own ORG_ADMIN; the platform routes are
	// gated on the SUPER_ADMIN role instead.
	PDF_SETTINGS_MANAGE = 'pdf_settings.manage',

	// Offer notification templates (TO-140): create, edit, deactivate and delete
	// the tenant's reusable notification messages. Held by the tenant's own
	// ORG_ADMIN; managers list, read and preview them through OFFER_WRITE.
	NOTIFICATION_TEMPLATES_MANAGE = 'notification_templates.manage',

	// Personal API keys (TO-516): list, mint (and relabel), and revoke the
	// caller's OWN keys. A key carries its owner's authority, so minting one is a
	// deliberate grant held by the tenant's ORG_ADMIN — unlike ATLAS, which gates
	// nothing. Split so a role could later read without minting.
	API_KEY_READ = 'api_key.read',
	API_KEY_CREATE = 'api_key.create',
	API_KEY_REVOKE = 'api_key.revoke',

	// GDPR compliance tooling (TO-232, ADR-0057): read requests and holds, run
	// erasures, place/release legal holds, and run subject access requests. All
	// tenant-scoped; held by the tenant's own ORG_ADMIN.
	COMPLIANCE_READ = 'compliance.read',
	COMPLIANCE_ERASURE_MANAGE = 'compliance.erasure.manage',
	COMPLIANCE_LEGAL_HOLD_MANAGE = 'compliance.legal_hold.manage',
	COMPLIANCE_SAR_MANAGE = 'compliance.sar.manage',
}
